import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import { getPlans, getPlanGenerationCheck, GenerationCheck } from '@/api/plansService';
import { checkSettings, CheckSettingsResult, Dow, EngineErrorBody, getDateBounds, getSettingsForm, RaceMode } from '@/api/engineService';
import { queryKeys } from '@/lib/queryKeys';
import { issueMessage } from '@/features/plans/issueMessages';
import { PLAN_TYPE_LABELS } from '@/features/plans/planAllocation';
import { DATE_STATE_CLASSES, DATE_STATE_LABELS, DAYS, dateStateOf, formatFr, settingsOf } from './simulatorUtils';

export interface FilterState {
  planId: string;
  today: string;
  competitionDate: string;
  mode: RaceMode;
  /** End of a recovery the athlete is still playing; pushes the whole calendar (R33). */
  recoveryEndsOn: string;
  /** sportId → N sessions per week, non-PPG sports only. */
  sessionsPerWeek: Record<string, number>;
  includePpg: boolean;
  /** `${dow}:${sportId}` keys */
  days: string[];
  brickDow: Dow | null;
}

const BIKE = 'CYCLING';

interface Props {
  state: FilterState;
  onChange: (next: FilterState) => void;
  onSimulate: () => void;
  simulating: boolean;
  error: EngineErrorBody | null;
  onCheckLoaded: (check: GenerationCheck | undefined) => void;
}

const MODES: { value: RaceMode; label: string }[] = [
  { value: 'ENROLL', label: 'Inscription' },
  { value: 'CONTINUE', label: 'Course déplacée en cours de préparation' },
  { value: 'MIGRATE', label: 'Migration V1' },
];

const Stepper = ({ value, min, max, onChange }: { value: number; min: number; max: number; onChange: (n: number) => void }) => (
  <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-800">
    <button type="button" className="px-2 py-1 text-gray-500 disabled:opacity-30" disabled={value <= min} onClick={() => onChange(value - 1)}>
      −
    </button>
    <span className="w-8 text-center text-sm tabular-nums">{value}</span>
    <button type="button" className="px-2 py-1 text-gray-500 disabled:opacity-30" disabled={value >= max} onClick={() => onChange(value + 1)}>
      +
    </button>
  </div>
);

const SimulatorFilters = ({ state, onChange, onSimulate, simulating, error, onCheckLoaded }: Props) => {
  const { data: plans } = useQuery({ queryKey: queryKeys.pv2Plans({ page: 1 }), queryFn: () => getPlans({ page: 1, pageSize: 100 }) });
  const { data: check } = useQuery({
    queryKey: queryKeys.pv2PlanGenerationCheck(state.planId),
    queryFn: () => getPlanGenerationCheck(state.planId),
    enabled: Boolean(state.planId),
  });
  const { data: bounds } = useQuery({
    queryKey: queryKeys.pv2DateBounds(state.planId, state.today, state.recoveryEndsOn || undefined),
    queryFn: () => getDateBounds(state.planId, state.today, state.recoveryEndsOn || undefined),
    enabled: Boolean(state.planId) && check?.planType === 'spe',
  });
  const { data: form } = useQuery({
    queryKey: queryKeys.pv2SettingsForm(state.planId),
    queryFn: () => getSettingsForm(state.planId),
    enabled: Boolean(state.planId),
  });

  useEffect(() => onCheckLoaded(check), [check, onCheckLoaded]);

  // Pre-fill the steppers with the plan defaults as soon as the bounds are known.
  useEffect(() => {
    if (!form || form.dayMode !== 'ATHLETE') return;
    const missing = form.sports.filter((s) => state.sessionsPerWeek[s.sportId] === undefined);
    if (missing.length === 0) return;
    onChange({
      ...state,
      sessionsPerWeek: { ...Object.fromEntries(form.sports.map((s) => [s.sportId, s.default])), ...state.sessionsPerWeek },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const [feasibility, setFeasibility] = useState<CheckSettingsResult | null>(null);
  useEffect(() => {
    if (!state.planId || !form || form.dayMode === 'FIXED') {
      setFeasibility(null);
      return;
    }
    const handle = setTimeout(() => {
      checkSettings(state.planId, settingsOf(state))
        .then(setFeasibility)
        .catch(() => setFeasibility(null));
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.planId, form, JSON.stringify(state.sessionsPerWeek), JSON.stringify(state.days), state.brickDow, state.includePpg]);

  const set = (patch: Partial<FilterState>) => onChange({ ...state, ...patch });
  const toggleDay = (dow: Dow, sportId: string) => {
    const key = `${dow}:${sportId}`;
    set({ days: state.days.includes(key) ? state.days.filter((d) => d !== key) : [...state.days, key] });
  };

  const setN = (sportId: string, n: number) => set({ sessionsPerWeek: { ...state.sessionsPerWeek, [sportId]: n } });

  /**
   * R47: `DAYS_ONLY` asks for one day set, stored against **every** sport of the plan — that is
   * what lets the engine run its ordinary per-sport machinery with identical sets.
   */
  const daysOnlySports = useMemo(() => (check?.sports ?? []).map((s) => s.id), [check]);
  const daysOnlySelected = useMemo(
    () => DAYS.map((d) => d.value).filter((dow) => daysOnlySports.every((sportId) => state.days.includes(`${dow}:${sportId}`))),
    [daysOnlySports, state.days]
  );
  const toggleDayOnly = (dow: Dow) => {
    const on = daysOnlySelected.includes(dow);
    const keys = daysOnlySports.map((sportId) => `${dow}:${sportId}`);
    set({ days: on ? state.days.filter((k) => !keys.includes(k)) : [...state.days.filter((k) => !keys.includes(k)), ...keys] });
  };

  // Only sports the athlete actually trains get a day row; PPG appears when the switch is on.
  const sports = useMemo(() => {
    if (!check || !form) return [];
    return check.sports.filter((s) =>
      s.id === check.ppgSportId ? state.includePpg : (state.sessionsPerWeek[s.id] ?? 0) >= 1
    );
  }, [check, form, state.includePpg, state.sessionsPerWeek]);

  // R48: a brick day is asked as soon as a bike session can be spent on the enchaînement. With
  // N(run) = 0 it degrades to the bike alone on that day, so the day is still needed (amends R30).
  const bricksPossible = Boolean(form?.hasBricks) && (state.sessionsPerWeek[BIKE] ?? 0) >= 1;

  const realBounds = bounds && 'reducedFromWeek' in bounds ? bounds : null;
  const dateState = realBounds && state.competitionDate ? dateStateOf(state.competitionDate, realBounds) : null;
  const isSpe = check?.planType === 'spe';
  const canSimulate =
    Boolean(state.planId) &&
    Boolean(check) &&
    (!isSpe || Boolean(state.competitionDate)) &&
    (check?.dayMode === 'FIXED' || (feasibility !== null && feasibility.ok && feasibility.feasibility.feasible));

  return (
    <Card className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Select label="Plan *" value={state.planId} onChange={(e) => set({ planId: e.target.value, days: [], brickDow: null })}>
          <option value="">— Choisir —</option>
          {(plans?.items ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName} · {PLAN_TYPE_LABELS[p.planType]}
              {p.generable ? '' : ' · non générable'}
            </option>
          ))}
        </Select>
        <Input label="Date du jour" type="date" value={state.today} onChange={(e) => set({ today: e.target.value })} />
        {isSpe && (
          <div>
            <Input
              label="Récupération en cours jusqu'au"
              type="date"
              value={state.recoveryEndsOn}
              onChange={(e) => set({ recoveryEndsOn: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-400">
              Simule un athlète qui termine un objectif précédent : le plan spé ne peut pas démarrer avant.
            </p>
          </div>
        )}
        {isSpe && (
          <div>
            <Input
              label="Date de compétition *"
              type="date"
              value={state.competitionDate}
              min={realBounds?.condensedFromWeek ?? realBounds?.reducedFromWeek}
              max={realBounds ? realBounds.latestWeek : undefined}
              onChange={(e) => set({ competitionDate: e.target.value })}
            />
            {realBounds && (
              <p className="mt-1 text-xs text-gray-400">
                Entre le {formatFr(realBounds.condensedFromWeek ?? realBounds.reducedFromWeek)} et le {formatFr(realBounds.latestWeek)} · durée idéale{' '}
                {realBounds.dmaxWeeks} sem.
              </p>
            )}
          </div>
        )}
        {isSpe && (
          <div>
            <Select label="Mode" value={state.mode} onChange={(e) => set({ mode: e.target.value as RaceMode })}>
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
            {state.mode === 'CONTINUE' && (
              <p className="mt-1 text-xs text-gray-400">Simule une préparation déjà commencée : pas de refus, pas de règle de semaines paires.</p>
            )}
          </div>
        )}
      </div>

      {realBounds && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">Semaine de compétition :</span>
          <span className={clsx('rounded px-2 py-0.5', DATE_STATE_CLASSES.disabled)}>avant le {formatFr(realBounds.condensedFromWeek ?? realBounds.reducedFromWeek)} refusée</span>
          {realBounds.condensedFromWeek && (
            <span className={clsx('rounded px-2 py-0.5', DATE_STATE_CLASSES.condensed)}>dès le {formatFr(realBounds.condensedFromWeek)} condensée</span>
          )}
          <span className={clsx('rounded px-2 py-0.5', DATE_STATE_CLASSES.reduced)}>dès le {formatFr(realBounds.reducedFromWeek)} réduite</span>
          <span className={clsx('rounded px-2 py-0.5', DATE_STATE_CLASSES.ideal)}>dès le {formatFr(realBounds.idealFromWeek)} idéale</span>
          <span className={clsx('rounded px-2 py-0.5', DATE_STATE_CLASSES.disabled)}>après le {formatFr(realBounds.latestWeek)} refusée</span>
          {dateState && (
            <Badge color={dateState === 'ideal' ? 'green' : dateState === 'disabled' ? 'red' : 'yellow'}>
              Date choisie : {DATE_STATE_LABELS[dateState]}
            </Badge>
          )}
        </div>
      )}

      {form && form.dayMode === 'ATHLETE' && (
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Séances par semaine *</p>
            <div className="flex flex-wrap gap-4">
              {form.sports.map((s) => (
                <label key={s.sportId} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  {s.label}
                  <Stepper
                    value={state.sessionsPerWeek[s.sportId] ?? s.default}
                    min={s.min}
                    max={s.max}
                    onChange={(n) => setN(s.sportId, n)}
                  />
                </label>
              ))}
            </div>
          </div>
          {form.ppg.available && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={state.includePpg} onChange={(e) => set({ includePpg: e.target.checked })} />
              PPG
            </label>
          )}
        </div>
      )}

      {check && check.dayMode === 'FIXED' && <p className="text-sm text-gray-500">Plan à jours fixes : jours définis dans les cycles.</p>}

      {check && check.dayMode === 'DAYS_ONLY' && (
        <div>
          <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Jours disponibles *</p>
          <div className="flex flex-wrap items-center gap-3">
            {DAYS.map((d) => (
              <label key={d.value} className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={daysOnlySelected.includes(d.value)} onChange={() => toggleDayOnly(d.value)} />
                {d.short}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            R47 : l'athlète ne choisit que ses jours. Le même jeu de jours vaut pour tous les sports du plan, et toutes les séances de la
            semaine sont conservées.
          </p>
        </div>
      )}

      {check && check.dayMode === 'ATHLETE' && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-8">
          <div className="overflow-x-auto">
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Jours par sport *</p>
            <table className="text-sm">
              <thead>
                <tr>
                  <th className="pr-3 text-left text-xs font-medium text-gray-500" />
                  {DAYS.map((d) => (
                    <th key={d.value} className="w-9 text-center text-xs font-medium text-gray-500" title={d.label}>
                      {d.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sports.map((s) => (
                  <tr key={s.id}>
                    <td className="pr-3 text-gray-700 dark:text-gray-200">{s.label}</td>
                    {DAYS.map((d) => (
                      <td key={d.value} className="text-center">
                        <input
                          type="checkbox"
                          checked={state.days.includes(`${d.value}:${s.id}`)}
                          onChange={() => toggleDay(d.value, s.id)}
                        />
                      </td>
                    ))}
                    <td className="pl-3 text-xs text-gray-400">
                      {s.id === check.ppgSportId
                        ? `${state.days.filter((k) => k.endsWith(`:${s.id}`)).length} jour(s) ouvert(s)`
                        : `${state.sessionsPerWeek[s.id] ?? 0} séance(s) · ${state.days.filter((k) => k.endsWith(`:${s.id}`)).length} jour(s) ouvert(s)`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {bricksPossible && (
            <div className="w-56">
              <Select label="Jour d'enchaînement *" value={state.brickDow ?? ''} onChange={(e) => set({ brickDow: e.target.value ? (Number(e.target.value) as Dow) : null })}>
                <option value="">— Choisir —</option>
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-gray-400">Jour à part : il ne porte que l'enchaînement les semaines concernées.</p>
            </div>
          )}
        </div>
      )}

      {feasibility && (
        <p className={clsx('text-sm', feasibility.ok && feasibility.feasibility.feasible ? 'text-success-600' : 'text-error-600')}>
          {feasibility.ok
            ? feasibility.feasibility.feasible
              ? 'Ces réglages tiennent dans la semaine.'
              : `Avec 2 séances maximum par jour, tes séances ne rentrent pas dans ta semaine. Ouvre au moins un jour de plus pour : ${(feasibility.feasibility.suggestedSportIds ?? []).join(', ')}.`
            : feasibility.error.message}
        </p>
      )}

      {error && (
        <div className="rounded-lg border border-error-500/40 bg-error-500/5 p-3 text-sm text-error-600">
          <p className="font-medium">{error.message ?? 'Simulation impossible.'}</p>
          {error.error?.issues && (
            <ul className="mt-1 list-disc pl-5 text-xs">
              {error.error.issues.map((issue, i) => (
                <li key={i}>{issueMessage(issue, issue.cycleId ? check?.cycleNames[issue.cycleId] : undefined)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onSimulate} disabled={!canSimulate || simulating}>
          {simulating ? 'Simulation…' : 'Simuler'}
        </Button>
      </div>
    </Card>
  );
};

export default SimulatorFilters;
